<template>
    <AppDialog
        :value="value"
        size="lg"
        @input="$emit('input', $event)"
        @close="onDialogClose"
    >
        <template #header>
            <h2 class="text-xl font-semibold text-theme-primary mb-2">
                Todas las herramientas
            </h2>
        </template>

        <div class="mb-6">
            <CustomInput
                id="tools-catalog-search"
                v-model="keySearch"
                type="text"
                placeholder="¿Qué estás buscando...?"
            >
                <template #icon>🔍</template>
            </CustomInput>
        </div>

        <div v-if="keySearch">
            <h3 class="text-lg font-semibold text-theme-primary mb-3">
                Resultados
            </h3>

            <div
                v-if="searchResults.length"
                class="grid grid-cols-1 sm:grid-cols-2 gap-2"
            >
                <ToolCard
                    v-for="link in searchResults"
                    :key="link.url"
                    :link="link"
                    :is-favorite="isFavorite(link)"
                    :is-expanded="expandedUrl === link.url"
                    @expand="onExpand"
                    @click="onNavigate"
                    @middle-click="onMiddleClick"
                    @toggle-favorite="onToggleFavorite"
                />
            </div>

            <p v-else class="text-theme-muted mt-4">
                No se encontraron herramientas para "{{ keySearch }}".
            </p>
        </div>

        <div
            v-else
            @click.self="collapseExpanded"
            @keydown.esc.stop.prevent="collapseExpanded"
        >
            <section v-if="cardLinksFavorites.length" class="mb-8">
                <h3 class="text-lg font-semibold text-theme-primary mb-3">
                    Favoritos
                </h3>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <ToolCard
                        v-for="link in cardLinksFavorites"
                        :key="link.url"
                        :link="link"
                        :is-favorite="true"
                        :is-expanded="expandedUrl === link.url"
                        @expand="onExpand"
                        @click="onNavigate"
                        @middle-click="onMiddleClick"
                        @toggle-favorite="onToggleFavorite"
                    />
                </div>
            </section>

            <section v-if="cardLinksReviewed.length" class="mb-8">
                <h3 class="text-lg font-semibold text-theme-primary mb-3">
                    Recientes
                </h3>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <ToolCard
                        v-for="link in cardLinksReviewed.slice(0, 6)"
                        :key="link.url"
                        :link="link"
                        :is-favorite="isFavorite(link)"
                        :is-expanded="expandedUrl === link.url"
                        @expand="onExpand"
                        @click="onNavigate"
                        @middle-click="onMiddleClick"
                        @toggle-favorite="onToggleFavorite"
                    />
                </div>
            </section>

            <section>
                <h3 class="text-lg font-semibold text-theme-primary mb-3">
                    Todas las herramientas
                </h3>
                <div v-for="group in groupedTools" :key="group.id" class="mb-6">
                    <h4 class="text-sm font-semibold text-theme-muted mb-2">
                        {{ group.icon }} {{ group.name }}
                    </h4>
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <ToolCard
                            v-for="link in group.links"
                            :key="link.url"
                            :link="link"
                            :is-favorite="isFavorite(link)"
                            :is-expanded="expandedUrl === link.url"
                            @expand="onExpand"
                            @click="onNavigate"
                            @middle-click="onMiddleClick"
                            @toggle-favorite="onToggleFavorite"
                        />
                    </div>
                </div>
            </section>
        </div>
    </AppDialog>
</template>

<script>
import { mapGetters, mapMutations, mapState } from "vuex";

import { homeCardGroups } from "@/common/fields/home-card-groups";

import AppDialog from "@/components/utils/AppDialog.vue";
import CustomInput from "@/components/utils/CustomInput.vue";
import ToolCard from "@/components/utils/ToolCard.vue";

export default {
    name: "ToolsCatalogModal",
    components: {
        AppDialog,
        CustomInput,
        ToolCard
    },
    model: {
        prop: "value",
        event: "input"
    },
    props: {
        value: { type: Boolean, required: true }
    },
    emits: ["input"],
    data: () => ({
        keySearch: "",
        expandedUrl: null
    }),
    computed: {
        ...mapState("control", {
            allCardLinksAvailable: "cardLinksAvailable",
            cardLinksReviewed: "cardLinksReviewed",
            cardLinksFavorites: "cardLinksFavorites"
        }),
        ...mapGetters("control", ["isCardLinkVisible"]),
        // Sin las herramientas de features que el tenant tiene apagadas.
        cardLinksAvailable() {
            return this.allCardLinksAvailable.filter(this.isCardLinkVisible);
        },
        searchResults() {
            const lower = this.keySearch.toLowerCase();
            return this.cardLinksAvailable.filter((item) =>
                item.name.toLowerCase().includes(lower)
            );
        },
        groupedTools() {
            return homeCardGroups
                .map((group) => ({
                    ...group,
                    links: this.cardLinksAvailable.filter(
                        (link) => (link.group || "other") === group.id
                    )
                }))
                .filter((group) => group.links.length);
        }
    },
    methods: {
        ...mapMutations("control", [
            "PUSH_RECENT_LINK",
            "TOGGLE_FAVORITE_LINK"
        ]),
        isFavorite(link) {
            return this.cardLinksFavorites.some(
                (item) => item.url === link.url
            );
        },
        onExpand(link) {
            this.expandedUrl = link.url;
        },
        collapseExpanded() {
            this.expandedUrl = null;
        },
        onToggleFavorite(link) {
            this.TOGGLE_FAVORITE_LINK(link);
        },
        pushLink(link) {
            setTimeout(() => {
                this.PUSH_RECENT_LINK(link);
            }, 100);
        },
        onNavigate(link) {
            this.pushLink(link);
            this.$emit("input", false);
            this.$router.push(link.url);
        },
        onMiddleClick(link) {
            this.pushLink(link);
            window.open(this.$router.resolve(link.url).href, "_blank");
        },
        onDialogClose() {
            this.keySearch = "";
            this.expandedUrl = null;
        }
    }
};
</script>
